import { PortalModule } from '@angular/cdk/portal';
import { Component, DestroyRef, DOCUMENT, ElementRef, HostBinding, Inject, inject, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterOutlet } from '@angular/router';

import { Environment } from '@fatigue/environments/env';
import { filter, map, take } from 'rxjs';

import { HeaderService } from './core/api/grpc';
import { ENVIRONMENT } from './core/providers/environment.provider';
import { PostHogService } from './core/services/posthog.service';
import { SSRService } from './core/services/ssr.service';
import { SettingActionsService } from './core/store/actions/setting.actions';
import { SettingSelectService } from './core/store/selectors/setting.selector';
import { TAppType } from './core/store/states/setting.state';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { LabelDevDirective } from './shared/directives/label-dev.directive';

@Component({
  selector: 'mark-cdr-app',
  template: `<router-outlet></router-outlet>
    @if (isLoading || isInitializedLoading) {
      <mark-cdr-loader></mark-cdr-loader>
    } `,
  hostDirectives: [LabelDevDirective],
  imports: [RouterOutlet, FormsModule, ReactiveFormsModule, LoaderComponent, PortalModule],
  standalone: true,
})
export class AppComponent {
  @HostBinding('attr.data-app-type') appType: TAppType = 'Desktop';

  isLoading: boolean = false;
  isInitializedLoading: boolean = false;

  private _zoomDisabled: boolean = false;
  private readonly _appTypes: Array<TAppType> = ['Desktop', 'Mobile'];
  private readonly _destroyRef = inject(DestroyRef);

  constructor(
    private _elementRef: ElementRef,
    @Inject(ENVIRONMENT) private _env: Environment,
    @Inject(DOCUMENT) private _document: Document,
    private _title: Title,
    private _SSRService: SSRService,
    private _settingActions: SettingActionsService,
    private _activatedRoute: ActivatedRoute,
    private _settingSelect: SettingSelectService,
    private readonly _headerService: HeaderService,
    private _ngZone: NgZone,
    private _postHogService: PostHogService,
  ) {
    if (this._SSRService.isBrowserOnly) {
      const typeAttribute = this._elementRef.nativeElement.getAttribute('type');
      if (typeAttribute) {
        const _typeAttribute = this._appTypes.find((x) => x.toLocaleLowerCase() === typeAttribute.toLocaleLowerCase());
        if (_typeAttribute) {
          this.appType = _typeAttribute;
          this._settingActions.setAppState(_typeAttribute);
          if (_typeAttribute === 'Mobile') {
            const markDomain = this._elementRef.nativeElement.getAttribute('domain');
            if (markDomain) {
              sessionStorage.setItem('mark-domain', markDomain);
            }
            // const language = this._elementRef.nativeElement.getAttribute('lang');
          }
        }
      }
      if (window.location.hostname === 'localhost') {
        window.location.href = `http://local.fatigue.sandbox.mark.health:5024`;
      }
    }
    // this._headerService.tokenUser =
    //   'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI1Y2M4Y2I1MC04MzE3LTRlNGYtYWJjNi1hMTNkMzI0ODRmNjgiLCJzZXNzaW9uSWQiOiI5NzQ2MjUwOC01MWM5LTQ0NmYtOGE1MS1kZGU2N2NhYzhiZjMiLCJuYmYiOjE3NTQ5OTM3ODMsImV4cCI6MTc3MDU0NTc4MywiaWF0IjoxNzU0OTkzNzgzfQ.ANi7buhDyp_fBSEGDAZtvv6dTqET1B4OzTMxu_newOk';
    this._activatedRoute.queryParams
      .pipe(
        map((x) => x['isMobile'] === 'true'),
        filter((x) => x),
        take(1),
      )
      .subscribe(() => {
        this._settingActions.setAppState('Mobile');
      });
    this._settingSelect
      .getAppState()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((appType) => {
        this.appType = appType!;
        if (this.appType === 'Mobile') {
          this.disableZoom();
        }
      });
    // if (this._SSRService.isBrowserOnly) {
    //   this._settingActions.checkUser();
    // }
  }

  public get elementRefElement(): HTMLElement | undefined {
    const elem = this._elementRef?.nativeElement;
    if (elem && elem.shadowRoot) {
      return elem.shadowRoot;
    }
    return elem;
  }

  private disableZoom(): void {
    if (this._zoomDisabled) {
      return;
    }
    this._zoomDisabled = true;
    this._ngZone.run(() => {
      this._document.addEventListener('DOMContentLoaded', () => {
        this._document.addEventListener('keydown', (event) => {
          if (event.ctrlKey === true) {
            switch (event.code) {
              case 'NumpadAdd':
              case 'NumpadSubtract':
              case 'Minus':
              case 'Equal':
                event.preventDefault();
                break;
              default:
                break;
            }
          }
        });
        ['mouse-wheel', 'wheel', 'mousewheel', 'DOMMouseScroll'].forEach((type) => {
          this._document.addEventListener(
            type,
            (event: any) => {
              if (event.ctrlKey === true) {
                event.preventDefault();
                event.stopPropagation();
              }
            },
            { passive: false },
          );
        });
      });

      // https://github.com/phparkle/disable-scroll-wheel-zoom
      // for Chrome scroll zoom (Ctrl + wheel) is disable
      this._document.addEventListener(
        'wheel',
        (e) => {
          if (!e.ctrlKey) {
            return;
          }
          e.preventDefault();
          window.scrollBy(e.deltaX, e.deltaY);
        },
        { passive: false },
      );

      this._document.addEventListener(
        'touchmove',
        (e: TouchEvent) => {
          if (e.touches.length > 1) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        { passive: false },
      );
    });
  }
}
